import Icon from '../icons/icon';
import classNames from 'classnames';
import { Card } from '../card/card';
import { usePopper } from 'react-popper';
import { useState } from 'react';
import { Spinner } from '../spinner/spinner';
import { Field, FieldProps } from './field';
import { Typography } from '../typography/typography';
import { usePlacesAutocomplete, UsePlacesAutocompleteProps } from './hooks/use-places-autocomplete';

export interface PlacesAutocompleteProps
  extends Omit<FieldProps, 'type'>,
    Pick<UsePlacesAutocompleteProps, 'apiType' | 'onPredictionSelected'> {}

export const PlacesAutocomplete = ({
  onPredictionSelected,
  onChange,
  value,
  className,
  apiType = 'admin',
  ...props
}: PlacesAutocompleteProps) => {
  const [inputWrapperRef, setInputWrapperRef] = useState<HTMLDivElement | null>(null);
  const [dropdownContainerRef, setDropdownContainerRef] = useState<HTMLDivElement | null>(null);

  const {
    // State
    inputText,
    predictions,
    isOpen,
    selectedIndex,
    isPredictionsLoading,
    isPlaceDetailsLoading,

    // Actions
    handleInputChange,
    handlePredictionClick,
  } = usePlacesAutocomplete({
    onPredictionSelected,
    value,
    onChange,
    inputRef: { current: inputWrapperRef },
    apiType,
  });

  const { styles, attributes } = usePopper(inputWrapperRef, dropdownContainerRef, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
    ],
  });

  return (
    <div className={classNames(className, 'relative')}>
      <div ref={setInputWrapperRef}>
        <Field
          dataCy="places-autocomplete-input"
          type="text"
          value={inputText}
          onChange={handleInputChange}
          disabled={isPlaceDetailsLoading}
          {...props}
        />
      </div>

      {(isPredictionsLoading || isPlaceDetailsLoading) && (
        <div className="absolute right-3 bottom-3 z-20">
          <Spinner />
        </div>
      )}

      {isOpen && inputWrapperRef && predictions.length > 0 && (
        <div
          ref={setDropdownContainerRef}
          {...attributes.popper}
          style={{ ...styles.popper, zIndex: 1000, width: '100%' }}
        >
          <Card className="max-h-80 !p-0 !rounded-lg flex flex-col overflow-hidden">
            <div className="flex-1 overflow-auto p-1">
              {/* Predictions */}
              <div className="flex flex-col gap-1">
                {predictions.map((prediction, index) => {
                  if (!prediction) return null;

                  return (
                    <div
                      key={prediction.placeId}
                      className={classNames('px-2 py-1 cursor-pointer rounded-md hover:bg-grey-100', {
                        '!bg-grey-100': index === selectedIndex,
                      })}
                      onClick={() => handlePredictionClick(prediction)}
                    >
                      <Typography type="text-sm" weight="medium" className="text-quipli-black-800">
                        {prediction.structuredFormat?.mainText?.text || prediction.text?.text}
                      </Typography>
                      {prediction.structuredFormat?.secondaryText?.text && (
                        <Typography type="text-xs" className="text-quipli-black-700">
                          {prediction.structuredFormat.secondaryText.text}
                        </Typography>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Google Attribution */}
            <div className="px-3 py-2 border-t border-grey-200 bg-grey-50 flex-shrink-0 rounded-b-lg">
              <div className="flex items-center justify-between text-xs text-quipli-black-300">
                <Typography type="text-xs" weight="medium">
                  Powered by
                </Typography>
                <div className="flex items-center">
                  <Icon icon="Google" className="w-4 h-4 mr-1" fill="var(--quipli-black-300)" data-cy="google-icon" />
                  <Typography type="text-xs" weight="medium">
                    Google
                  </Typography>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
